import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { createContext, memo, ReactNode, useContext } from "react";
import { TARGET_BILLING_DAY_OPTIONS } from "./constants";

/**
 * True when the team being edited is on unified billing. In that case
 * per-product `targetBillingDay` and the legacy
 * `billingSettings.firstBillingDayOfMonth` are owned by the team-wide
 * alignment work (M-align-write) and cannot be edited from the admin UI.
 * The server enforces the same constraint via `putTeam`,
 * `updateBillingSettings`, and `updateConversionBillingSettings`.
 */
export const BillingDayLockedContext = createContext<boolean>(false);

export const BILLING_DAY_LOCK_TOOLTIP =
  "Locked: managed by unified billing. Disable unified billing on the General tab to change the billing day.";

/**
 * Wraps an arbitrary input so it picks up the lock tooltip on hover when
 * the team is on unified billing. The contained input is responsible for
 * disabling itself via `useContext(BillingDayLockedContext)` (or by
 * receiving an explicit `disabled` prop). Pass `locked` explicitly to
 * override the context — e.g., from a form input value that the user
 * is editing live.
 */
export const BillingDayLockedTooltip = memo(function BillingDayLockedTooltip({
  children,
  locked,
}: {
  children: ReactNode;
  locked?: boolean;
}) {
  const contextLocked = useContext(BillingDayLockedContext);
  const effectivelyLocked = locked ?? contextLocked;
  if (!effectivelyLocked) {
    return <>{children}</>;
  }
  return (
    <Tooltip arrow placement="top" title={BILLING_DAY_LOCK_TOOLTIP}>
      <span>{children}</span>
    </Tooltip>
  );
});

/**
 * Shared per-product target-billing-day dropdown. Reads
 * `BillingDayLockedContext` and disables itself + adds the lock tooltip
 * when the team is on unified billing. `enabled` is the per-product
 * enabled flag (e.g., the marketing email billing toggle) — when that is
 * off the dropdown is also disabled, independent of the lock.
 */
export const TargetBillingDayDropdown = memo(function TargetBillingDayDropdown({
  enabled,
  value,
  onChange,
  label = "Target billing day",
  description = "Change the billing day of the month (leave empty to keep current day)",
}: {
  enabled: boolean;
  value: number | null | undefined;
  onChange: (value: number) => void;
  label?: string;
  description?: string;
}) {
  const locked = useContext(BillingDayLockedContext);
  const dropdown = (
    <RedoSingleSelectDropdownInput<number>
      disabled={locked || !enabled}
      options={TARGET_BILLING_DAY_OPTIONS}
      optionSelected={({ item }) => onChange(item.value)}
      placeholder="Select day (1-28)"
      selectedItem={TARGET_BILLING_DAY_OPTIONS.find((o) => o.value === value)}
    />
  );
  return (
    <LabeledInput description={description} label={label}>
      <BillingDayLockedTooltip>{dropdown}</BillingDayLockedTooltip>
    </LabeledInput>
  );
});
